/**
 * The state of a narrated recording, kept outside the panel that shows it.
 *
 * On a phone the record panel lives in a sheet the coach closes to see the
 * board, and a small Stop pill stands in for it meanwhile. Both read and drive
 * this one recording, so closing the sheet never stops or forgets anything.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { WebFileExporter, type PlayRecorder, type RecordingResult } from '../adapters/index.ts';
import { drawBoardFrame, type BoardFrame } from './recordingFrame.ts';

export type RecordingPhase = 'idle' | 'recording' | 'preparing';

export interface RecordingApi {
  readonly phase: RecordingPhase;
  readonly supported: boolean;
  readonly secureContext: boolean;
  readonly withAudio: boolean;
  setWithAudio(value: boolean): void;
  /** Milliseconds since the recording started. */
  readonly elapsed: number;
  /** How far preparing the video is, 0..1. */
  readonly progress: number;
  readonly result: RecordingResult | null;
  readonly resultUrl: string | null;
  readonly notice: string | null;
  readonly error: string | null;
  /** True when the last recording could not be prepared and is kept for another try. */
  readonly canRetry: boolean;
  start(): Promise<void>;
  stop(): Promise<void>;
  /** Prepares the kept recording again, with no need to record anew. */
  retry(): Promise<void>;
  save(): Promise<void>;
}

function safeFilename(name: string): string {
  return name.trim().replace(/[^\w\d-]+/g, '-').replace(/-+/g, '-').toLowerCase() || 'play';
}

export function formatClock(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function useRecording(
  recorder: PlayRecorder<BoardFrame>,
  /** What the video is called when it is shared — asked at that moment, not when recording began. */
  playName: () => string,
  /** Told when a recording starts and stops, so the board logs its frames meanwhile. */
  onRecordingChange?: (recording: boolean) => void,
): RecordingApi {
  const exporter = useMemo(() => new WebFileExporter(), []);

  const [phase, setPhase] = useState<RecordingPhase>('idle');
  const [progress, setProgress] = useState(0);
  const [supported, setSupported] = useState(true);
  const isRecording = phase === 'recording';
  const [withAudio, setWithAudio] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState<RecordingResult | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const startedAtRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    void recorder.isSupported().then((ok) => {
      if (!cancelled) setSupported(ok);
    });
    return () => {
      cancelled = true;
    };
  }, [recorder]);
  const secureContext = typeof window !== 'undefined' ? window.isSecureContext : true;

  useEffect(() => {
    onRecordingChange?.(isRecording);
  }, [isRecording, onRecordingChange]);

  useEffect(() => {
    if (!isRecording) return;
    const handle = setInterval(() => setElapsed(Date.now() - startedAtRef.current), 200);
    return () => clearInterval(handle);
  }, [isRecording]);

  useEffect(() => {
    return () => {
      if (resultUrl) URL.revokeObjectURL(resultUrl);
    };
  }, [resultUrl]);

  const start = useCallback(async () => {
    setError(null);
    setNotice(null);
    if (resultUrl) URL.revokeObjectURL(resultUrl);
    setResult(null);
    setResultUrl(null);

    try {
      const { hasAudio } = await recorder.start({ withAudio, draw: drawBoardFrame });
      startedAtRef.current = Date.now();
      setElapsed(0);
      setPhase('recording');
      if (withAudio && !hasAudio) {
        setNotice('No microphone available — recording video only.');
      }
    } catch (cause) {
      setError((cause as Error).message);
    }
  }, [recorder, withAudio, resultUrl]);

  // Stop, or Try again after a recording could not be prepared: either way
  // the video is made from the recording, with the progress bar showing.
  const prepare = useCallback(
    async (again: boolean) => {
      setError(null);
      setProgress(0);
      setPhase('preparing');
      try {
        const recording = again ? await recorder.retry(setProgress) : await recorder.stop(setProgress);
        setResult(recording);
        setResultUrl(URL.createObjectURL(recording.blob));
      } catch (cause) {
        setError((cause as Error).message);
      } finally {
        setPhase('idle');
      }
    },
    [recorder],
  );
  const stop = useCallback(() => prepare(false), [prepare]);
  const retry = useCallback(() => prepare(true), [prepare]);

  const save = useCallback(async () => {
    if (!result) return;
    await exporter.saveBlob(result.blob, `${safeFilename(playName())}.${result.extension}`);
  }, [result, exporter, playName]);

  return {
    phase,
    supported,
    secureContext,
    withAudio,
    setWithAudio,
    elapsed,
    progress,
    result,
    resultUrl,
    notice,
    error,
    // Read from the recorder on every render: it changes when preparing fails,
    // which re-renders through `error`.
    canRetry: recorder.canRetry,
    start,
    stop,
    retry,
    save,
  };
}
